import type { NotificationChannel } from '@wundo/shared/notificationChannel.ts';
import type {
  NotificationMeeting,
  NotificationRecipient,
  NotificationSkipReason,
} from '@/lib/notificationSend.ts';
import { formatDate } from '@/utils/dateUtils.ts';

export const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  msteams: 'Teams',
  slack: 'Slack',
  email: 'Email',
  none: 'No channel',
};

/*
 * Why somebody a staff member ticked was left out. `msteams`, `slack` and
 * `email` are the reasons a notification does go, so they never appear here;
 * they are named only because the reason is one list.
 */
const SKIP_LABELS: Record<NotificationSkipReason, string> = {
  not_found: 'no longer exists',
  settled: 'has already received it',
  cannot_see_roleplay: 'does not have this roleplay on their practice page',
  already_played: 'already did this roleplay',
  msteams: 'was queued',
  slack: 'was queued',
  email: 'was queued',
  no_teams_chat: 'has no Teams chat with Wundo',
  no_slack_chat: 'has no Slack DM with Wundo',
  teams_only: 'is not on Teams',
  teams_or_email_only: 'is on Slack, and this goes by Teams or email only',
  no_webapp: 'is on an organisation that does not use the web app',
  no_email_address: 'has no email address',
  opted_out: 'unsubscribed from Wundo emails',
  channel_none: 'has no notification channel',
  magic_link_disabled: 'is on an organisation that signs in with SSO',
  expired: 'has a closed account: their pilot ended',
  send_failed: 'could not be mailed: sending failed',
};

/* "unsubscribed from Wundo emails on 12 Oct 2026": staff see since when. */
export function skipLabel(
  reason: NotificationSkipReason,
  person?: NotificationRecipient,
): string {
  if (reason === 'opted_out' && person?.email_opt_out_at) {
    return `${SKIP_LABELS.opted_out} on ${unsubscribedOn(person.email_opt_out_at)}`;
  }
  return SKIP_LABELS[reason];
}

function unsubscribedOn(iso: string): string {
  return formatDate(iso, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function nameOf(person: NotificationRecipient): string {
  return (
    [person.first_name, person.last_name].filter(Boolean).join(' ') ||
    person.email ||
    person.user_id
  );
}

export function titleOf(meeting: NotificationMeeting): string {
  return meeting.title || 'Untitled';
}
